import React from "react";
import { MetricCard } from "@/components/ui/metric-card";
import { LoadingOverlay } from "@/components/ui/loading-overlay";
import { 
  Activity, 
  DollarSign, 
  Ticket, 
  BadgePercent, 
  BadgeDollarSign,
  Clock,
  CheckCircle,
  TrendingUp
} from "lucide-react";

interface CouponMetricsCardSectionProps {
  totalRevenue: number;
  totalCoupons: number;
  couponsUsed: number;
  couponsActive: number;
  couponsPending: number;
  conversionRate: number;
  averageTicket: number;
  couponROI: number;
  formatCurrency: (value: number) => string;
  isLoadingMetrics: boolean;
}

// Memoized card components
const RevenueCard = React.memo(({ totalRevenue, formatCurrency }: { totalRevenue: number; formatCurrency: (value: number) => string }) => (
  <MetricCard
    title="Receita Total"
    value={formatCurrency(totalRevenue)}
    icon={<DollarSign className="h-4 w-4" />}
    variant="primary"
  />
));

const TotalCouponsCard = React.memo(({ totalCoupons }: { totalCoupons: number }) => (
  <MetricCard
    title="Total de Cashback"
    value={totalCoupons.toLocaleString()}
    icon={<Ticket className="h-4 w-4" />}
    variant="secondary"
  />
));

const UsedCouponsCard = React.memo(({ couponsUsed }: { couponsUsed: number }) => (
  <MetricCard
    title="Cashback Utilizado"
    value={couponsUsed.toLocaleString()}
    icon={<CheckCircle className="h-4 w-4" />}
    variant="success"
  />
));

const ActiveCouponsCard = React.memo(({ couponsActive }: { couponsActive: number }) => (
  <MetricCard
    title="Cashback Ativo"
    value={couponsActive.toLocaleString()}
    icon={<Activity className="h-4 w-4" />}
    variant="info"
  />
));

const PendingCouponsCard = React.memo(({ couponsPending }: { couponsPending: number }) => (
  <MetricCard
    title="Cashback Pendente"
    value={couponsPending.toLocaleString()}
    icon={<Clock className="h-4 w-4" />}
    variant="warning"
  />
));

const ConversionRateCard = React.memo(({ conversionRate }: { conversionRate: number }) => (
  <MetricCard
    title="Taxa de Conversão"
    value={`${conversionRate.toFixed(1)}%`}
    icon={<BadgePercent className="h-4 w-4" />}
    variant="accent"
  />
));

const AverageTicketCard = React.memo(({ averageTicket, formatCurrency }: { averageTicket: number; formatCurrency: (value: number) => string }) => (
  <MetricCard
    title="Ticket Médio"
    value={formatCurrency(averageTicket)}
    icon={<BadgeDollarSign className="h-4 w-4" />}
    variant="primary"
  />
));

const CouponROICard = React.memo(({ couponROI }: { couponROI: number }) => (
  <MetricCard
    title="ROI do Cashback"
    value={`${couponROI.toFixed(1)}x`}
    icon={<TrendingUp className="h-4 w-4" />}
    variant="success"
  />
));

const CouponMetricsCardSection: React.FC<CouponMetricsCardSectionProps> = ({
  totalRevenue,
  totalCoupons,
  couponsUsed,
  couponsActive,
  couponsPending,
  conversionRate,
  averageTicket,
  couponROI,
  formatCurrency,
  isLoadingMetrics
}) => {
  return (
    <div className="relative">
      <LoadingOverlay isLoading={isLoadingMetrics}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <RevenueCard totalRevenue={totalRevenue} formatCurrency={formatCurrency} />
        <TotalCouponsCard totalCoupons={totalCoupons} />
        <UsedCouponsCard couponsUsed={couponsUsed} />
        <ConversionRateCard conversionRate={conversionRate} />
        <AverageTicketCard averageTicket={averageTicket} formatCurrency={formatCurrency} />
        <CouponROICard couponROI={couponROI} />
        </div>
      </LoadingOverlay>
    </div>
  );
};

export default React.memo(CouponMetricsCardSection);